import { useEffect } from 'react';
import { ArrowRightIcon, PhoneIcon } from 'lucide-react';
import { business, images, routes } from '../data/site';
import { SiteLink } from '../components/SiteLink';
import { Accordion, AccordionItem } from '../components/Accordion';
import { testingProducts } from '../data/testingProducts';
import { spaChemicalProducts } from '../data/spaChemicalProducts';
const TITLE = 'How to Take Care of Your Hot Tub | Pegasus Spas Brooklyn, MI';
const DESCRIPTION = 'Simple hot tub care from our Brooklyn, Michigan showroom: water, filters, draining, covers, winter care and supplies. Free basic water testing, and we will teach you even if you bought elsewhere.';
const CANONICAL = 'https://www.pegasus-spas.com/spas.html';

/** Real product photography from our catalog. */
const strips = testingProducts[0];
const taylorKit = testingProducts[2];
const shock = spaChemicalProducts[7];

/** The care hub. Each topic gives one plain answer and points to where the detail lives. */
const careTopics = [{
  title: 'Water',
  body: 'Test once or twice each week, then sanitize and shock as your care guide directs.',
  link: {
    label: 'The basics',
    href: '#basics'
  }
}, {
  title: 'Filters',
  body: 'Rinse and clean the filter as directed, and replace it when it is worn out.',
  link: {
    label: 'Shop filters',
    href: routes.filters
  }
}, {
  title: 'Draining & Refilling',
  body: 'Fresh water every so often keeps care simple. We can walk you through it or do it for you.',
  link: {
    label: 'Ask our service team',
    href: routes.service
  }
}, {
  title: 'Covers',
  body: 'A cover that fits and seals well keeps heat in and debris out.',
  link: {
    label: 'Build a replacement cover',
    href: routes.covers
  }
}, {
  title: 'Winter & Seasonal',
  body: 'Keep it running through the cold, or have us winterize and bag it for you.',
  link: {
    label: 'Winterizing and service',
    href: routes.service
  }
}, {
  title: 'Supplies',
  body: 'Chemicals, test strips, filters and cleaning tools, in the showroom and online.',
  link: {
    label: 'Supplies & chemicals',
    href: routes.supplies
  }
}, {
  title: 'Problems',
  body: 'Not heating, losing water, jets acting up? Tell us what it is doing.',
  link: {
    label: 'Get service help',
    href: routes.service
  }
}];
const basics = [{
  title: 'Test the water once or twice each week.',
  body: 'A quick check tells you where things stand before anything gets away from you.'
}, {
  title: 'Sanitize as directed so the water stays ready to use.',
  body: 'This is the part that keeps the spa pleasant to step into on a Tuesday night.'
}, {
  title: 'Shock as directed when the water-care plan calls for it.',
  body: 'Follow the care guide for your spa rather than guessing at the timing.'
}, {
  title: 'Rinse and clean the filter as directed so water can move properly.',
  body: 'A clogged filter makes everything else work harder than it needs to.'
}];
const waterQuestions: AccordionItem[] = [{
  question: 'The water looks cloudy',
  answer: 'Cloudiness can come from several different things, so this is a good moment to stop and check rather than add something. Bring us a sample and tell us what the spa has been doing.'
}, {
  question: 'Foam keeps coming back',
  answer: 'Foam that returns after you deal with it usually means something else is going on underneath. Let us test the water before you keep treating the foam.'
}, {
  question: 'The water smells different',
  answer: 'A change in smell is worth a test rather than a guess. Bring a fresh sample in and we will look at it with you.'
}, {
  question: 'I am not sure which product to use',
  answer: 'That is a completely normal question and an easy one to answer in person. Tell us what you have on the shelf at home and we will help you sort out what to use and when.'
}, {
  question: 'How do I find the right filter or replacement part?',
  answer: 'Bring the existing part or send clear photos of the part and its label. If a part number or spa model would help, we can show you where to find it before you order.'
}, {
  question: 'Where can I ask about warranty, moving a spa or what came with my purchase?',
  answer: 'Call or text us. Those details depend on your spa, purchase and location, so we will look at your situation rather than give a one-size-fits-all answer.'
}];
const relatedLinks = [{
  label: 'Service & Repair',
  href: routes.service
}, {
  label: 'Service Work Guarantee',
  href: routes.serviceGuarantee
}, {
  label: 'Supplies & Chemicals',
  href: routes.supplies
}, {
  label: 'Contact Us',
  href: routes.contact
}];
function upsertMeta(name: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
const linkClass = 'inline-flex min-h-[44px] items-center gap-1.5 text-[16px] font-bold text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta';
export function SpaCarePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = TITLE;
    upsertMeta('description', DESCRIPTION);
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', CANONICAL);
  }, []);
  return <main data-id="element-2055" data-mp="3ih11dfyadwwh">
      {/* Opening with the reassurance up front */}
      <section aria-labelledby="spa-care-h1" className="w-full bg-white" data-id="element-2056" data-mp="3iils0uhhcvp2">
        <div className="mx-auto max-w-container px-5 pb-10 pt-10 sm:px-8 lg:pb-12 lg:pt-14" data-id="element-2057" data-mp="3nh5h33rdenln">
          <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-12" data-id="element-2058" data-mp="1nd2wjpqpbgpf">
            <div className="lg:col-span-7" data-id="element-2059" data-mp="1jd295flmjv68">
              <p className="text-[14px] font-bold uppercase tracking-[0.02em] text-pegasus-blue" data-id="element-2060" data-mp="1je3wojjtua4u">
                Spa Care
              </p>
              <h1 id="spa-care-h1" className="mt-3 max-w-2xl font-display text-h1-mobile font-semibold text-ink lg:text-[48px] lg:leading-[56px]" data-id="element-2061" data-mp="1sd7e7j3cihh8">
                How Do I Take Care of My Hot Tub?
              </h1>
              <p className="mt-5 max-w-2xl border-l-2 border-pegasus-magenta pl-5 font-display text-[22px] font-semibold leading-8 text-ink sm:text-[25px] sm:leading-9" data-id="element-2062" data-mp="224v43fk97qgl">
                We&rsquo;ll teach you. And you don&rsquo;t need to have bought your spa here.
              </p>
              <p className="mt-4 max-w-2xl text-body text-ink/80 sm:text-body-lg" data-id="element-2063" data-mp="lp0tdj3upp2z">
                We walk every new owner through the basics at delivery, and we are here afterward
                when a question comes up. If you feel unsure, call or text us before you guess.
              </p>

              <div className="mt-7 flex flex-wrap items-center gap-3" data-id="element-2064" data-mp="2xbchzoid6u6m">
                <SiteLink to={business.phoneHref} className="inline-flex min-h-[48px] items-center gap-2 rounded-btn bg-pegasus-magenta px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-2065" data-mp="2jl90tbycdqo1">
                  <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-2066" data-mp="2elw5s3xbhdad" />
                  Call or Text {business.phone}
                </SiteLink>
                <a href="#water-testing" className="inline-flex min-h-[48px] items-center rounded-btn border border-ink/25 bg-white px-5 text-[16px] font-medium text-ink transition-colors duration-150 ease-out hover:border-pegasus-blue" data-id="element-2067" data-mp="20nejman288uk">
                  Free Water Testing
                </a>
              </div>
            </div>

            <div className="lg:col-span-5" data-id="element-2068" data-mp="2mqjim368alw5">
              <div className="rounded-card bg-water px-6 py-7 sm:px-8" data-id="element-2069" data-mp="2hsror3m0wppb">
                <div className="flex items-end justify-center gap-5 sm:gap-8" data-id="element-2070" data-mp="2htjj6b3vwhe0">
                  <img src={strips.image} alt={strips.alt} className="h-24 w-auto object-contain sm:h-32" decoding="async" data-id="element-2071" data-mp="2d0qe1m4i771e" />
                  <img src={taylorKit.image} alt={taylorKit.alt} className="h-32 w-auto object-contain sm:h-44" decoding="async" data-id="element-2072" data-mp="23za4thpfdayr" />
                  <img src={shock.image} alt={shock.alt} className="h-24 w-auto object-contain sm:h-32" decoding="async" data-id="element-2073" data-mp="1p4cbalijifg3" />
                </div>
                <p className="mt-5 text-center text-[15px] leading-6 text-ink/65" data-id="element-2074" data-mp="3jjew45wn99co">
                  Testing supplies and water-care products we carry in Brooklyn.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Care hub */}
      <section aria-labelledby="hub-heading" className="w-full border-t border-ink/10 bg-white" data-id="element-2075" data-mp="4n3552xmflyr">
        <div className="mx-auto max-w-container px-5 pb-12 pt-10 sm:px-8 lg:pb-16" data-id="element-2076" data-mp="9k3b7duh0opv">
          <h2 id="hub-heading" className="font-display text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-2077" data-mp="390ry90s3kx1m">
            Care, Topic by Topic
          </h2>

          <ul className="mt-7 grid grid-cols-1 border-t border-ink/15 sm:grid-cols-2 lg:grid-cols-4" data-id="element-2078" data-mp="3i0exph19nss1">
            {careTopics.map(topic => <li key={topic.title} className="flex flex-col border-b border-ink/15 py-5 sm:pr-8" data-id="element-2079" data-mp="3n1cg9uq6uou2">
                <h3 className="text-[19px] font-semibold leading-7 text-ink" data-id="element-2080" data-mp="129al18zdl0qf">{topic.title}</h3>
                <p className="mt-1.5 text-[16px] leading-6 text-ink/75" data-id="element-2081" data-mp="1un8mdv6ws8t">{topic.body}</p>
                <div className="mt-auto pt-2" data-id="element-2082" data-mp="6thx9wfu9b0u">
                  <SiteLink to={topic.link.href} className={linkClass} data-id="element-2083" data-mp="6s6spuekjz8o">
                    {topic.link.label}
                    <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-2084" data-mp="bkq5ip5fmqcf" />
                  </SiteLink>
                </div>
              </li>)}
          </ul>
        </div>
      </section>

      {/* Water testing, the prominent offer */}
      <section id="water-testing" aria-labelledby="testing-heading" className="w-full scroll-mt-28 bg-ink" data-id="element-2085" data-mp="3jzi0u6p19yy1">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-2086" data-mp="3o01xl95p30jn">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-center lg:gap-14" data-id="element-2087" data-mp="3t1ra6t8kfm9u">
            <div className="lg:col-span-6" data-id="element-2088" data-mp="13c5qyfv96712">
              <h2 id="testing-heading" className="font-display text-h2-mobile font-semibold text-white lg:text-h2-desktop" data-id="element-2089" data-mp="13dgwol89dout">
                Bring Us a Water Sample
              </h2>
              <p className="mt-4 text-body text-white/85 sm:text-body-lg" data-id="element-2090" data-mp="1dfkwj6zjj5o7">
                Bring a fresh sample to the Brooklyn showroom and tell us what the water has been
                doing. We will explain the result and help you choose the next step, wherever you
                bought your spa.
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-3" data-id="element-2091" data-mp="zo69mp2iikbv">
                <SiteLink to={routes.location} className="inline-flex min-h-[48px] items-center rounded-btn bg-pegasus-magenta px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-2092" data-mp="up34z5qg2g4f">
                  Hours &amp; Directions
                </SiteLink>
                <SiteLink to={routes.testing} className="inline-flex min-h-[48px] items-center rounded-btn border-2 border-white/70 px-5 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-white hover:text-ink" data-id="element-2093" data-mp="2k2gagyaama74">
                  Shop Testing Supplies
                </SiteLink>
              </div>
            </div>

            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:col-span-6" data-id="element-2094" data-mp="4g4531l7kht9">
              <div className="rounded-card bg-white px-6 py-6" data-id="element-2095" data-mp="8hfup743131g">
                <dt className="text-[17px] font-semibold leading-6 text-ink" data-id="element-2096" data-mp="w5z1fi7hh7rf">Basic water testing</dt>
                <dd className="mt-3 font-display text-[44px] font-semibold leading-none text-pine" data-id="element-2097" data-mp="1153e4itm9nmi">
                  Free
                </dd>
              </div>
              <div className="rounded-card bg-white px-6 py-6" data-id="element-2098" data-mp="n50xezx1p08u">
                <dt className="text-[17px] font-semibold leading-6 text-ink" data-id="element-2099" data-mp="s3lvotsql9un">
                  Detailed computerized testing
                </dt>
                <dd className="mt-3 font-display text-[36px] font-semibold leading-none text-ink sm:text-[40px]" data-id="element-2100" data-mp="37xma719ljapz">
                  Small fee
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </section>

      {/* Basics */}
      <section id="basics" aria-labelledby="basics-heading" className="w-full scroll-mt-28 bg-water" data-id="element-2101" data-mp="30b8mvixtp1x2">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-2102" data-mp="3e9p2y0hks4rv">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-14" data-id="element-2103" data-mp="39d84odgnwa12">
            <div className="lg:col-span-4" data-id="element-2104" data-mp="2lo5ola1oep8o">
              <h2 id="basics-heading" className="font-display text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-2105" data-mp="2lkrne54r0m2t">
                Four Simple Spa-Care Basics
              </h2>
              <p className="mt-4 text-body text-ink/80" data-id="element-2106" data-mp="2qlp4sfhxpc39">
                Check the water once or twice each week; the timing of the other steps depends on
                your care guide. We go through all of this with you at delivery.
              </p>
              <figure className="mt-7" data-id="element-2107" data-mp="1xx9to41xbdx7">
                <div className="aspect-[3/2] w-full overflow-hidden rounded-card bg-white" data-id="element-2108" data-mp="116og01ez5p0q">
                  <img src={images.sundanceFilter} alt="A woman lifting a filter cartridge out of a hot tub to rinse it" className="h-full w-full object-cover object-center" loading="lazy" decoding="async" data-id="element-2109" data-mp="164zoa7pmfrgu" />
                </div>
                <figcaption className="mt-2 text-[14px] leading-5 text-ink/60" data-id="element-2110" data-mp="1jw4gwpuknjho">
                  Sundance Spas water-care photography
                </figcaption>
              </figure>
            </div>

            <ol className="divide-y divide-ink/15 border-y border-ink/15 lg:col-span-8" data-id="element-2111" data-mp="1tvbtvbxe9c49">
              {basics.map((step, index) => <li key={step.title} className="flex items-baseline gap-5 py-5 sm:gap-7" data-id="element-2112" data-mp="3vm6uch2jpzhb" data-mpi={index}>
                  <span aria-hidden="true" className="shrink-0 font-display text-[30px] font-semibold leading-none text-pine sm:text-[36px]" data-id="element-2113" data-mp="3vojkhhigjg5g">
                    {index + 1}
                  </span>
                  <div data-id="element-2114" data-mp="3ncrcqe66t1jp">
                    <h3 className="text-[18.5px] font-semibold leading-7 text-ink sm:text-[20px]" data-id="element-2115" data-mp="3nbg686lcxfow">
                      {step.title}
                    </h3>
                    <p className="mt-1.5 text-[16.5px] leading-7 text-ink/75" data-id="element-2116" data-mp="3edw4yxnhvjea">{step.body}</p>
                  </div>
                </li>)}
            </ol>
          </div>
        </div>
      </section>

      {/* Questions */}
      <section aria-labelledby="water-heading" className="w-full bg-white" data-id="element-2117" data-mp="9zfx10nn4p7u">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-2118" data-mp="5vq644jhtvjn">
          <h2 id="water-heading" className="font-display text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-2119" data-mp="yrebsnay0om">
            When Something Doesn&rsquo;t Look Right
          </h2>
          <p className="mt-4 max-w-2xl text-body text-ink/80 sm:text-body-lg" data-id="element-2120" data-mp="2lne5s7pw6265">
            Pick whatever sounds closest. A photo helps, but a water sample tells us more.
          </p>

          <div className="mt-7 max-w-4xl" data-id="element-2121" data-mp="2s3e9346dufgb">
            <Accordion items={waterQuestions} defaultOpenIndex={-1} data-id="element-2122" data-mp="2s2cvarxzfeuw" />
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-3" data-id="element-2123" data-mp="2e4ems36cecnb">
            <SiteLink to={business.phoneHref} className="inline-flex min-h-[48px] items-center gap-2 rounded-btn bg-pegasus-magenta px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-2124" data-mp="2i58aowjsbrg7">
              <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-2125" data-mp="1ugyp9vqhh5pl" />
              Still Unsure? Call or Text Us
            </SiteLink>
          </div>
        </div>
      </section>

      {/* Related links */}
      <section aria-label="Related pages" className="w-full border-t border-ink/10 bg-white" data-id="element-2126" data-mp="1lfh43yy7ydu1">
        <div className="mx-auto max-w-container px-5 py-6 sm:px-8" data-id="element-2127" data-mp="1qetyd4rf6n6n">
          <ul className="flex flex-col gap-x-8 gap-y-1 sm:flex-row sm:flex-wrap sm:items-center" data-id="element-2128" data-mp="1qh6j1pokbbne">
            {relatedLinks.map(link => <li key={link.label} data-id="element-2129" data-mp="11v0f19qsoqmm">
                <SiteLink to={link.href} className="inline-flex min-h-[44px] items-center gap-1.5 text-[16px] font-medium text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-2130" data-mp="11w1q3e7t9d4c">
                  {link.label}
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-2131" data-mp="1ksa0ewfoke6i" />
                </SiteLink>
              </li>)}
          </ul>
        </div>
      </section>
    </main>;
}